/**
 * useUndoState - Undo/Redo functionality with 5-second window
 * 
 * Created: 08-10-2025 13:45:12
 * Purpose: Provides undo capability for destructive operations with temporary storage
 * 
 * Features:
 * - 5-second undo window for user corrections
 * - SessionStorage for temporary data persistence
 * - Audit trail compliance (undo logs as DELETE + CREATE)
 * - Toast notification integration
 * 
 * Usage:
 * ```typescript
 * const { registerUndo, executeUndo, canUndo } = useUndoState();
 * 
 * // On delete operation
 * registerUndo('assignment', deletedData, () => recreateAssignment(deletedData));
 * 
 * // User clicks [Undo] button
 * executeUndo();
 * ```
 * 
 * @module hooks/useUndoState
 */

import { useState, useCallback, useEffect, useRef } from 'react';

// =============================================================================
// TYPES AND INTERFACES
// =============================================================================

export interface UndoOperation<T = any> {
  /** Type of operation (assignment, block, etc.) */
  type: string;
  
  /** Original data before deletion/modification */
  data: T;
  
  /** Function to execute when undo is triggered */
  undoFn: () => void | Promise<void>;
  
  /** Timestamp when operation was registered */
  timestamp: number;
  
  /** Expiry timestamp (timestamp + 5000ms) */
  expiresAt: number;
}

export interface UndoStateReturn {
  /** Register a new undo operation */
  registerUndo: <T>(type: string, data: T, undoFn: () => void | Promise<void>) => void;
  
  /** Execute the current undo operation */
  executeUndo: () => Promise<void>;
  
  /** Check if undo is available */
  canUndo: boolean;
  
  /** Current undo operation (null if none) */
  currentUndo: UndoOperation | null;
  
  /** Clear the current undo operation */
  clearUndo: () => void;
  
  /** Remaining time in seconds (for UI countdown) */
  remainingSeconds: number;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const UNDO_WINDOW_MS = 5000; // 5 seconds
const SESSION_STORAGE_KEY = 'mchd_undo_operation';

// =============================================================================
// HOOK IMPLEMENTATION
// =============================================================================

/**
 * useUndoState Hook
 * 
 * Manages undo state with 5-second window and sessionStorage persistence.
 * Automatically clears undo after expiry.
 * 
 * @returns {UndoStateReturn} Undo state and control functions
 */
export function useUndoState(): UndoStateReturn {
  const [currentUndo, setCurrentUndo] = useState<UndoOperation | null>(() => {
    // Initialize from sessionStorage if available and not expired
    try {
      const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (stored) {
        const operation: UndoOperation = JSON.parse(stored);
        if (Date.now() < operation.expiresAt) {
          return operation;
        } else {
          // Expired, remove from storage
          sessionStorage.removeItem(SESSION_STORAGE_KEY);
        }
      }
    } catch (error) {
      console.error('Failed to load undo state from sessionStorage:', error);
    }
    return null;
  });
  
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Clear all undo timers
   */
  const clearTimers = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  /**
   * Clear the current undo operation
   */
  const clearUndo = useCallback(() => {
    clearTimers();
    setCurrentUndo(null);
    setRemainingSeconds(0);
    try {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    } catch (error) {
      console.error('Failed to remove undo state from sessionStorage:', error);
    }
  }, [clearTimers]);

  /**
   * Register a new undo operation
   * 
   * Note: undoFn is not persisted to sessionStorage (functions cannot be serialized).
   * This means undo will not work across page refreshes, which is acceptable for a
   * 5-second window.
   * 
   * @param type - Type of operation (e.g., 'assignment', 'block')
   * @param data - Original data before deletion
   * @param undoFn - Function to execute when undo is triggered
   */
  const registerUndo = useCallback(<T,>(
    type: string,
    data: T,
    undoFn: () => void | Promise<void>
  ) => {
    // Clear any existing undo
    clearTimers();

    const now = Date.now();
    const operation: UndoOperation<T> = {
      type,
      data,
      undoFn,
      timestamp: now,
      expiresAt: now + UNDO_WINDOW_MS
    };

    // Set state
    setCurrentUndo(operation as UndoOperation);
    setRemainingSeconds(5);

    // Persist to sessionStorage (without undoFn)
    try {
      const storable = {
        type,
        data,
        timestamp: operation.timestamp,
        expiresAt: operation.expiresAt
      };
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(storable));
    } catch (error) {
      console.error('Failed to persist undo state to sessionStorage:', error);
    }

    // Set expiry timer
    timerRef.current = setTimeout(() => {
      clearUndo();
    }, UNDO_WINDOW_MS);

    // Set countdown timer (update every second)
    countdownRef.current = setInterval(() => {
      setRemainingSeconds((prev) => {
        const next = prev - 1;
        if (next <= 0) {
          if (countdownRef.current) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
          }
        }
        return Math.max(0, next);
      });
    }, 1000);
  }, [clearTimers, clearUndo]);

  /**
   * Execute the current undo operation
   * 
   * Calls the undoFn and clears the undo state.
   * Handles both synchronous and asynchronous undo functions.
   */
  const executeUndo = useCallback(async () => {
    if (!currentUndo) {
      console.warn('No undo operation available');
      return;
    }

    try {
      // Execute undo function
      await currentUndo.undoFn();
      
      // Clear undo after successful execution
      clearUndo();
    } catch (error) {
      console.error('Failed to execute undo:', error);
      // Still clear undo even if it fails
      clearUndo();
      throw error;
    }
  }, [currentUndo, clearUndo]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);

  return {
    registerUndo,
    executeUndo,
    canUndo: currentUndo !== null,
    currentUndo,
    clearUndo,
    remainingSeconds
  };
}

